import { useCallback, useEffect, useMemo, useState } from 'react'
import { DataResponse } from 'infrastructure/api'
import { useOverwriteSearchParams, useSearchParams } from 'shared/hooks'
import {
  atom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import { debounce } from '@mui/material'
import { csvMap } from 'shared/api/list/listQueries'

type orderValues = 'asc' | 'desc'
type sortType = { field: string; order: orderValues }
type filterValue = string | number | boolean
type filterType = Record<string, filterValue>

interface FetchParams {
  perPage: number
  currentPage: number
  filters: filterType
  sort: sortType
}

type resourceKey = 'page' | 'perPage'

const initialSort: sortType = { field: 'id', order: 'asc' }
const initialPage = 0
const initialPerPage = 25

const totalAtom = atom(0)

// Converts search param string to a number, returning the initial value if
// the search param value does not exist or if there's an issue converting
function useNumberConvert(initialValue: number) {
  return useCallback(
    (s: string | null) => {
      const num = Number(s ?? initialValue)
      return Number.isNaN(num) ? initialValue : num
    },
    [initialValue],
  )
}

function useListDefaults() {
  const setTotal = useSetAtom(totalAtom)
  const setSearchParams = useOverwriteSearchParams()
  return useCallback(() => {
    setSearchParams({
      sort_field: 'id',
      sort_order: 'asc',
    })
    setTotal(0)
  }, [setSearchParams, setTotal])
}

// wraps the work for converting and setting numeric search params (page, perPage)
function useSearchResource<T extends number>(
  key: resourceKey,
  stringConvert: (s: string | null) => T,
) {
  const [searchParams, setSearchParams] = useSearchParams()
  const stringVal = searchParams.get(key)
  return useMemo(() => {
    return {
      data: stringConvert(stringVal),
      onChange: (newVal: T) =>
        setSearchParams({
          [key]: String(newVal),
        }),
    }
  }, [key, stringVal, stringConvert, setSearchParams])
}

export function useListPage() {
  const convert = useNumberConvert(initialPage)
  const { data, onChange } = useSearchResource<number>('page', convert)
  return useMemo(() => {
    return { page: data, setPage: onChange }
  }, [data, onChange])
}

export function useListPerPage() {
  const convert = useNumberConvert(initialPerPage)
  const { data, onChange } = useSearchResource<number>('perPage', convert)
  return useMemo(() => {
    return { perPage: data, setPerPage: onChange }
  }, [data, onChange])
}

export function useListFilters() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => {
    const result: filterType = {}
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter.')) {
        const field = key.slice('filter.'.length)
        if (value === 'true') result[field] = true
        else if (value === 'false') result[field] = false
        else result[field] = value
      }
    })
    return result
  }, [searchParams])

  return useMemo(() => {
    const setFilter = (field: string, value: filterValue | undefined) => {
      setSearchParams({
        [`filter.${field}`]: value === undefined ? undefined : String(value),
      })
    }
    return { filters, setFilter }
  }, [filters, setSearchParams])
}

function parseOrder(value: string | null): orderValues {
  if (value === 'asc' || value === 'desc') return value
  return initialSort.order
}

export function useListSort() {
  const [searchParams, setSearchParams] = useSearchParams()
  const field = searchParams.get('sort_field') ?? initialSort.field
  const order = parseOrder(searchParams.get('sort_order'))
  return useMemo(() => {
    const sortToggle = (newField: string) => {
      if (newField !== field) {
        setSearchParams({ sort_field: newField, sort_order: 'asc' })
      } else {
        setSearchParams({
          sort_field: field,
          sort_order: order === 'asc' ? 'desc' : 'asc',
        })
      }
    }
    return { field, order, sortToggle }
  }, [field, order, setSearchParams])
}

export function useListPagination() {
  const { page, setPage } = useListPage()
  const { perPage, setPerPage } = useListPerPage()
  const total = useAtomValue(totalAtom)

  return useMemo(() => {
    return {
      page,
      onPageChange: setPage,
      rowsPerPage: perPage,
      onRowsPerPageChange: setPerPage,
      total,
    }
  }, [page, setPage, perPage, setPerPage, total])
}

// useListResource requires just a fetchFunc to provide what's needed for a paginated ResourceList
// onChangeFunc is called whenever data is fetched, this allows for selected item state to be reset
export function useListResource<T>(
  fetchFunc: (params: FetchParams) => Promise<DataResponse<T>>,
  onChangeFunc?: () => void,
) {
  const { page, rowsPerPage: perPage } = useListPagination()
  const { field, order } = useListSort()
  const { filters } = useListFilters()
  const [data, setData] = useState<T[]>([])
  const setTotal = useSetAtom(totalAtom)
  const reset = useListDefaults()

  // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce wrapper is intentionally stable; setData/setTotal are React state setters
  const debounced = useCallback(
    debounce(async (params: FetchParams) => {
      const resp = await fetchFunc(params).catch(reset)
      setData(resp?.data ?? [])
      setTotal(resp?.total_count ?? 0)
    }, 800),
    [fetchFunc, reset],
  )

  const doFetch = useCallback(async () => {
    await debounced({
      perPage,
      currentPage: page + 1,
      filters,
      sort: { field, order },
    })
    onChangeFunc?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setTotal is not used directly here
  }, [debounced, perPage, page, field, order, filters, onChangeFunc])

  useEffect(() => {
    doFetch().then()
  }, [doFetch])

  return useMemo(() => {
    return {
      data,
      reFetch: doFetch,
    }
  }, [data, doFetch])
}

export function useCSVDownload(resourceKey: string) {
  const { filters } = useListFilters()
  // check if resource is able to download csvs
  if (!(resourceKey in csvMap)) {
    return
  }
  const csvFunc = csvMap[resourceKey]
  // CSV functions only use string filter values; numeric/boolean values are coerced to strings at the API boundary
  const stringFilters = Object.fromEntries(
    Object.entries(filters).map(([k, v]) => [k, String(v)]),
  )
  return () => csvFunc(stringFilters)
}
